import { useAppContext } from "@ciq/shared/hooks/context"
import { useCallback, useMemo, useRef, useState } from "react"
import { fetchGroupedJobHistory } from "./api"

const EMPTY_SEMESTERS = []

/**
 * @typedef {Object} UseGroupedJobHistoryResult
 * @property {boolean} isLoaded - Boolean indicating if the grouped data is loaded
 * @property {Array<Object>} dates - Array of date cards with job chains
 * @property {string|null} expandedDate - Currently expanded date (null for summary view)
 * @property {Array<Object>} semesters - Array of semester objects with id, name, start_date, end_date
 * @property {Object} actions - Collection of functions for managing grouped view
 * @property {() => void} actions.loadDates - Fetch grouped job history from the API
 * @property {(date: string|null) => void} actions.setExpandedDate - Expand/collapse a date card
 * @property {() => void} actions.refresh - Reload the data
 */

/**
 * Custom hook for managing grouped SPARK job history by date and chain.
 * Provides date-based cards with summary status and drill-down capability.
 *
 * @returns {UseGroupedJobHistoryResult} Hook result with grouped data and actions
 */
export function useGroupedJobHistory() {
  const { schoolId, school } = useAppContext()
  const semesters = school?.semesters || EMPTY_SEMESTERS
  const [state, setState] = useState({
    isLoaded: false,
    dates: [],
    expandedDate: null,
    filter: "all", // "all" | "running" | "issues"
    filterCounts: { all: 0, running: 0, failures: 0, missing: 0, stale: 0, issues: 0 },
  })
  const abortControllerRef = useRef(null)

  const loadDates = useCallback(async () => {
    // Abort any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    try {
      const response = await fetchGroupedJobHistory(
        schoolId,
        { limit: 30 },
        abortControllerRef.current.signal
      )

      setState(prev => ({
        ...prev,
        isLoaded: true,
        dates: response.dates,
        filterCounts: response.filter_counts || { all: 0, running: 0, failures: 0, missing: 0, stale: 0, issues: 0 },
      }))
    } catch (error) {
      // Intentional cancellation - no action needed
      if (error.name === "AbortError") return

      // Already handled by shared error handler
      if (error.handled) return

      console.error(error)
      setState(prev => ({
        ...prev,
        isLoaded: true,
      }))
    }
  }, [schoolId])

  const actions = useMemo(
    () => ({
      loadDates,
      setExpandedDate: date => {
        setState(prev => ({
          ...prev,
          expandedDate: prev.expandedDate === date ? null : date,
        }))
      },
      setFilter: filter => {
        setState(prev => ({
          ...prev,
          filter,
        }))
      },
      refresh: loadDates,
    }),
    [loadDates]
  )

  // Filter dates based on selected filter using flags from API
  const filteredDates = useMemo(() => {
    if (state.filter === "all") {
      return state.dates
    }
    if (state.filter === "running") {
      return state.dates.filter(d => d.flags?.has_running)
    }
    if (state.filter === "issues") {
      return state.dates.filter(d =>
        d.flags?.has_failures ||
        d.flags?.has_missing ||
        d.flags?.has_stale
      )
    }
    return state.dates
  }, [state.dates, state.filter])

  return useMemo(
    () => ({
      isLoaded: state.isLoaded,
      dates: filteredDates,
      expandedDate: state.expandedDate,
      filter: state.filter,
      filterCounts: state.filterCounts,
      semesters,
      actions,
    }),
    [state.isLoaded, state.expandedDate, state.filter, state.filterCounts, filteredDates, semesters, actions]
  )
}
